import React, { ReactElement } from 'react';
import useStyles from './styles';
import { Control, DeepMap, FieldError, useForm } from 'react-hook-form';
import TranslatedText from '../../../../components/atoms/translated_text';
import { Button, Typography } from '@material-ui/core';
import {
  AppSelect,
  AppTextField,
  AppDatePicker,
  AppRadiogroup,
} from '../../../../components/molecules/form_controls';
import GridLayout from '../../../../components/molecules/grid_layout';
import { useDispatch } from 'react-redux';
import RequestInformationActions from 'redux/reducers/request_reducer/actions';
import * as RequestInfoSelectors from 'redux/reducers/request_reducer/selectors';
import { SchemeTypes } from 'config';
import { StateStatus } from 'redux/utils/common';

interface IProps {}

interface FormTypes {
  schemeType: number | null;
  memberState: number | null;
  vatNumber?: string | null;
  intNumber?: string | null;
  requestType: 'MSCON' | 'MSEST' | null;
  vatReturnReference: string | null;
  vatReturnVersion: string | null;
  vatReturnPeriodQuarter: number | null;
  vatReturnPeriodYear: number | null;
  country: number | null;
}

const defaultValues: FormTypes = {
  schemeType: null,
  memberState: null,
  vatNumber: null,
  intNumber: null,
  requestType: null,
  vatReturnReference: null,
  vatReturnVersion: null,
  vatReturnPeriodQuarter: null,
  vatReturnPeriodYear: null,
  country: null,
};

export default function VatReturnRequestForm(props: IProps): ReactElement {
  // console.log('Render VatReturnRequestForm');
  const classes = useStyles();
  const { errors, control, handleSubmit, reset, watch } = useForm<FormTypes>({
    defaultValues,
  });

  const watchIntNumber = watch('intNumber');
  const watchVatNumber = watch('vatNumber');

  const watchScheme = watch('schemeType');
  const dispatch = useDispatch();
  const status = RequestInfoSelectors.useGetVatReturnRequestStatus();

  React.useEffect(() => {
    if (status === StateStatus.Success) {
      reset(defaultValues);
    }
  }, [status]);

  React.useEffect(() => {
    dispatch(RequestInformationActions.fetchMemberStateAction());
    // dispatch(RequestInformationActions.fetchMemberStateExcludeCyprusAction());
  }, [dispatch]);

  const onSubmit = (data: FormTypes) => {
    dispatch(
      RequestInformationActions.vatReturnRequestAction({
        schemeType: data.schemeType as number,
        memberState: data.memberState as number,
        vatNumber: data.vatNumber as string,
        intNumber: data.intNumber as string,
        requestType: data.requestType as 'MSOCN' | 'MSEST',
        vatReturnReference: data.vatReturnReference as string,
        vatReturnVersion: data.vatReturnVersion as string,
        vatReturnPeriodQuarter: data.vatReturnPeriodQuarter as number,
        vatReturnPeriodYear: data.vatReturnPeriodYear as number,
        country: data.country as number,
      })
    );
  };

  return (
    <div>
      <Typography variant="h5" style={{ marginBottom: '16px' }}>
        <TranslatedText defaultText={'VatReturn Request Information'} />
      </Typography>
      <form
        className={classes.form}
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <GridLayout
          elements={[
            {
              element: renderSchemeType(errors, control),
              size: 12,
              id: 'scheme',
            },
            {
              element: renderMemberStateField(errors, control),
              size: 12,
              id: 'memberState',
            },
            {
              element: renderVatNumberField(
                errors,
                control,
                watchScheme === SchemeTypes.IOSS
                  ? 'Importer Number'
                  : 'VAT Number',
                watchIntNumber,
                watchScheme ?? -1
              ),
              size: 12,
              id: 'vatNumber',
            },
            ...(watchScheme === SchemeTypes.IOSS
              ? [
                  {
                    element: renderIntermediaryField(
                      errors,
                      control,
                      watchVatNumber
                    ),
                    size: 12,
                    id: 'intermediary',
                  } as any,
                ]
              : []),
            {
              element: renderRequestTypeField(errors, control),
              size: 12,
              id: 'requestType',
            },
            {
              element: renderVatReturnReferenceField(errors, control),
              size: 12,
              id: 'vatReturnReference',
            },
            {
              element: renderVatReturnVersionField(errors, control),
              size: 12,
              id: 'vatReturnVersion',
            },
            {
              element: renderPeriodQuarterField(errors, control, watchScheme),
              size: 6,
              id: 'vatReturnPeriodQuarter',
            },
            {
              element: renderPeriodYearField(errors, control),
              size: 6,
              id: 'vatReturnPeriodYear',
            },
            {
              element: renderCountryField(errors, control),
              size: 12,
              id: 'country',
            },
            {
              element: (
                <Button
                  className={classes.submitButton}
                  type="submit"
                  variant="contained"
                >
                  Submit Request
                </Button>
              ),
              size: 12,
              id: 'submit',
            },
          ]}
        />
      </form>
    </div>
  );
}

const renderSchemeType = (
  errors: DeepMap<FormTypes, FieldError>,
  control: Control<FormTypes>
) => {
  const select_countries = [
    {
      id: SchemeTypes.EU,
      label: 'MOSS',
    },
    {
      id: SchemeTypes.NON_EU,
      label: 'VOES',
    },
    {
      id: SchemeTypes.IOSS,
      label: 'IMPORT',
    },
  ];

  return (
    <AppSelect
      variant="outlined"
      fullWidth
      label={'NETP Type'}
      id="schemeType"
      name="schemeType"
      required
      error={'schemeType' in errors}
      control={control}
      rules={{ required: 'Required Field' }}
      options={select_countries as any}
      helperText={errors.schemeType ? errors.schemeType.message : ' '}
    />
  );
};

const renderMemberStateField = (
  errors: DeepMap<FormTypes, FieldError>,
  control: Control<FormTypes>
) => {
  let countries = RequestInfoSelectors.useGetMemberStateCountries();
  let select_countries = [];
  select_countries.push({
    id: null,
    label: 'Select member state country..',
  });
  if (countries && countries.length > 0)
    for (let country of countries)
      select_countries.push({
        id: country.country_id,
        label: `${country.code} - ${country.description}`,
      });

  return (
    <AppSelect
      variant="outlined"
      fullWidth
      label={'Member State'}
      id="memberState"
      name="memberState"
      required
      error={'memberState' in errors}
      control={control}
      rules={{ required: 'Required Field' }}
      options={select_countries as any}
      helperText={errors.memberState ? errors.memberState.message : ' '}
    />
  );
};

const renderVatNumberField = (
  errors: DeepMap<FormTypes, FieldError>,
  control: Control<FormTypes>,
  label: string,
  intNumber: string | null | undefined,
  schemeType: number
) => {
  return (
    <AppTextField
      variant="outlined"
      fullWidth
      label={label}
      id="vatNumber"
      name="vatNumber"
      required={schemeType === SchemeTypes.IOSS ? false : true}
      error={'vatNumber' in errors}
      control={control}
      rules={{
        validate: (value: any) => {
          if (!value && (!intNumber || intNumber === '')) {
            return schemeType !== SchemeTypes.IOSS
              ? 'Required Field'
              : 'At least one of Intermediary Number or Importer Number is required';
          }
          return true;
        },
      }}
      helperText={errors.vatNumber ? errors.vatNumber.message : ' '}
    />
  );
};

const renderIntermediaryField = (
  errors: DeepMap<FormTypes, FieldError>,
  control: Control<FormTypes>,
  vatNumber: string | null | undefined
) => {
  return (
    <AppTextField
      variant="outlined"
      fullWidth
      label={'Intermediary Number'}
      id="intNumber"
      name="intNumber"
      error={'intNumber' in errors}
      control={control}
      rules={{
        validate: (value: any) => {
          if (!value && (!vatNumber || vatNumber === '')) {
            return 'At least one of Intermediary Number or VAT Number is required';
          }
          return true;
        },
      }}
      helperText={errors.intNumber ? errors.intNumber.message : ' '}
    />
  );
};

const renderVatReturnReferenceField = (
  errors: DeepMap<FormTypes, FieldError>,
  control: Control<FormTypes>
) => {
  return (
    <AppTextField
      variant="outlined"
      fullWidth
      label={'VAT Return Reference'}
      id="vatReturnReference"
      name="vatReturnReference"
      error={'vatReturnReference' in errors}
      control={control}
      rules={{}}
      helperText={
        errors.vatReturnReference ? errors.vatReturnReference.message : ' '
      }
    />
  );
};

const renderVatReturnVersionField = (
  errors: DeepMap<FormTypes, FieldError>,
  control: Control<FormTypes>
) => {
  return (
    <AppTextField
      variant="outlined"
      fullWidth
      label={'VAT Return Version'}
      id="vatReturnVersion"
      name="vatReturnVersion"
      error={'vatReturnVersion' in errors}
      control={control}
      rules={{}}
      helperText={
        errors.vatReturnVersion ? errors.vatReturnVersion.message : ' '
      }
    />
  );
};

const renderPeriodQuarterField = (
  errors: DeepMap<FormTypes, FieldError>,
  control: Control<FormTypes>,
  watchScheme: any
) => {
  const quarters = [
    {
      id: '1',
      label: 'Q1',
    },
    {
      id: '2',
      label: 'Q2',
    },
    {
      id: '3',
      label: 'Q3',
    },
    {
      id: '4',
      label: 'Q4',
    },
  ];

  const months = [
    {
      id: '01',
      label: 'Jan',
    },
    {
      id: '02',
      label: 'Feb',
    },
    {
      id: '03',
      label: 'Mar',
    },
    {
      id: '04',
      label: 'Apr',
    },
    {
      id: '05',
      label: 'May',
    },
    {
      id: '06',
      label: 'Jun',
    },
    {
      id: '07',
      label: 'Jul',
    },
    {
      id: '08',
      label: 'Aug',
    },
    {
      id: '09',
      label: 'Sep',
    },
    {
      id: '10',
      label: 'Oct',
    },
    {
      id: '11',
      label: 'Nov',
    },
    {
      id: '12',
      label: 'Dec',
    },
  ];

  const select_quarters = watchScheme === SchemeTypes.IOSS ? months : quarters;

  return (
    <AppSelect
      variant="outlined"
      fullWidth
      label={'Period Quarter'}
      id=""
      name="vatReturnPeriodQuarter"
      error={'vatReturnPeriodQuarter' in errors}
      control={control}
      rules={{}}
      options={select_quarters as any}
      helperText={
        errors.vatReturnPeriodQuarter
          ? errors.vatReturnPeriodQuarter.message
          : ' '
      }
    />
  );
};

const renderPeriodYearField = (
  errors: DeepMap<FormTypes, FieldError>,
  control: Control<FormTypes>
) => {
  return (
    <AppDatePicker
      fullWidth
      format="yyyy"
      label={'Period Year'}
      error={'vatReturnPeriodYear' in errors}
      control={control}
      views={['year']}
      rules={{}}
      autoOk
      name="vatReturnPeriodYear"
      helperText={
        errors.vatReturnPeriodYear && errors.vatReturnPeriodYear.message
      }
    />
  );
};

const renderCountryField = (
  errors: DeepMap<FormTypes, FieldError>,
  control: Control<FormTypes>
) => {
  let countries = RequestInfoSelectors.useGetMemberStateCountries();
  let select_countries = [];
  select_countries.push({
    id: null,
    label: 'Select country..',
  });
  if (countries && countries.length > 0)
    for (let country of countries)
      select_countries.push({
        id: country.country_id,
        label: `${country.code} - ${country.description}`,
      });

  return (
    <AppSelect
      variant="outlined"
      fullWidth
      label={'Country Code (MSCON/MSEST)'}
      id="country"
      name="country"
      error={'country' in errors}
      control={control}
      rules={{}}
      options={select_countries as any}
      helperText={errors.country ? errors.country.message : ' '}
    />
  );
};

const renderRequestTypeField = (
  errors: DeepMap<FormTypes, FieldError>,
  control: Control<FormTypes>
) => {
  return (
    <AppRadiogroup
      row
      id="requestType"
      label="Request Type"
      error={'requestType' in errors}
      name="requestType"
      control={control}
      rules={{ required: 'Required Field' }}
      helperText={errors.requestType && errors.requestType.message}
      values={[
        {
          value: 'MSCON',
          label: 'Member State of Consumption',
        },
        {
          value: 'MSEST',
          label: 'Member State of Establishment',
        },
      ]}
    />
  );
};
